/**
 * J7.5 / v1.1.3-b1: in-app hundo celebration toast.
 *
 * Mounted once at the AppShell level (alongside the global Toaster). Polls
 * /api/hundo/feed via useHundoFeed and renders a neon-green celebration
 * card whenever a brand-new hundo lands. Auto-dismisses after 6 seconds or
 * when the trainer taps it (which navigates to the Hundo dex).
 *
 * Tone: friendly, kid-safe. No em dashes. No emojis. Black + neon green
 * brand.
 */
import { useEffect, useMemo, useState } from "react";
import { PokemonSilhouette } from "@/components/PokemonSilhouette";
import { findPokemon } from "@/lib/pokemon";
import { useHundoFeed, type HundoEventDTO } from "@/hooks/useHundoFeed";
import { SwipeDismissible } from "@/components/SwipeDismissible";

const AUTO_DISMISS_MS = 6000;

function speciesLabel(ev: HundoEventDTO): string {
  if (ev.speciesName && ev.speciesName.trim().length > 0) return ev.speciesName;
  const p = findPokemon(ev.speciesId);
  return p?.name ?? `Pokemon #${ev.speciesId}`;
}

export function HundoToast() {
  const { unseen, markAllSeen } = useHundoFeed();
  const [active, setActive] = useState<HundoEventDTO | null>(null);

  // Surface the newest unseen event. The feed comes back newest-first.
  const next = useMemo(() => unseen[0] ?? null, [unseen]);

  useEffect(() => {
    if (!next) return;
    if (active && active.id === next.id) return;
    setActive(next);
  }, [next, active]);

  useEffect(() => {
    if (!active) return;
    const t = window.setTimeout(() => {
      setActive(null);
      markAllSeen();
    }, AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [active, markAllSeen]);

  if (!active) return null;

  const name = speciesLabel(active);

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="hundo-toast"
      className="fixed left-1/2 z-[60] -translate-x-1/2 px-4"
      style={{
        bottom: "calc(var(--nx-safe-bottom) + 88px)",
        maxWidth: "min(96vw, 420px)",
        width: "100%",
      }}
    >
      <SwipeDismissible
        onDismiss={() => {
          setActive(null);
          markAllSeen();
        }}
        ariaLabel="Hundo notification. Swipe up to dismiss."
      >
      <button
        type="button"
        onClick={() => {
          setActive(null);
          markAllSeen();
          if (typeof window !== "undefined") {
            window.location.hash = "/?section=hundo-dex";
          }
        }}
        className="w-full text-left rounded-2xl border-2 border-[#39FF14] bg-black/95 px-4 py-3 shadow-[0_0_24px_rgba(57,255,20,0.55)] backdrop-blur"
      >
        <div className="flex items-center gap-3">
          <div className="shrink-0 rounded-xl border border-[#39FF14]/60 bg-black p-1">
            <PokemonSilhouette
              id={active.speciesId}
              size={48}
              caught
              shiny={active.isShiny}
              ariaLabel={`${name} sprite`}
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#39FF14]">
              Hundo unlocked
            </div>
            <div className="truncate text-base font-semibold text-white">
              {name} joined your dex
            </div>
            <div className="text-xs text-white/70">
              IV 15 / 15 / 15
              {active.cp ? ` . CP ${active.cp}` : ""}
              {active.isShiny ? " . shiny" : ""}
              {active.isLucky ? " . lucky" : ""}
            </div>
          </div>
        </div>
      </button>
      </SwipeDismissible>
    </div>
  );
}
